import React, { useState, useEffect, useCallback } from 'react';
import './Predictions.css';
import { API_BASE_URL } from '../config';
import { formatDateTimeRelative } from '../utils/timeFormat';
import { Moon, Bed } from 'phosphor-react';

interface Baby {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  is_owner?: boolean;
  owner_username?: string;
}

interface PredictedSlot {
  id: string;
  predicted_start_time: string;
  predicted_end_time: string;
  slot_type: 'nap' | 'night_sleep';
  confidence_score?: number;
  notes?: string;
}

interface PredictionResponse {
  id: string;
  baby_id: string;
  status: 'success' | 'error' | 'insufficient_data';
  created_at: string;
  reasoning?: string;
  predicted_slots: PredictedSlot[];
  error_message?: string;
  days_of_data?: number;
}

interface PredictionsProps {
  user: any;
}

const Predictions: React.FC<PredictionsProps> = ({ user }) => {
  const [babies, setBabies] = useState<Baby[]>([]);
  const [prediction, setPrediction] = useState<PredictionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Find active baby based on user's active_baby_id
  const activeBaby = babies.find(baby => baby.id === user.active_baby_id) || babies[0];

  const fetchBabies = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setBabies(data);
      }
    } catch (error) {
      console.error('Failed to fetch babies:', error);
    }
  }, [user.id]);

  const fetchLatestPrediction = useCallback(async () => {
    if (!activeBaby) return;

    setLoading(true);
    setError(null);

    try {
      // Get user's timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await fetch(`${API_BASE_URL}/api/sleep/predict/${activeBaby.id}/latest?timezone=${encodeURIComponent(timezone)}`);

      if (response.ok) {
        const data = await response.json();
        setPrediction(data);
      } else if (response.status === 404) {
        setError('Baby not found');
      } else {
        setError('Failed to load predictions');
      }
    } catch (error) {
      console.error('Failed to fetch prediction:', error);
      setError('Failed to load predictions');
    } finally {
      setLoading(false);
    }
  }, [activeBaby]);

  useEffect(() => {
    if (user) {
      fetchBabies();
    }
  }, [user, fetchBabies]);

  useEffect(() => {
    if (activeBaby) {
      fetchLatestPrediction();
    }
  }, [activeBaby, fetchLatestPrediction]);

  const formatDuration = (startTime: string, endTime: string): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    const hours = Math.floor(diffHours);
    const minutes = Math.round((diffHours - hours) * 60);

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  if (loading) {
    return (
      <div className="predictions-page">
        <div className="loading">Loading predictions (this may take 10-15 seconds)...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="predictions-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="predictions-page">
      <div className="predictions-header">
        <h2>Sleep Schedule</h2>
        <p className="baby-name">{activeBaby?.name || 'Loading...'}</p>
      </div>

      {!prediction ? (
        <div className="empty-state">
          <p>No sleep schedule available yet</p>
        </div>
      ) : prediction.status === 'insufficient_data' ? (
        <div className="info-state">
          <p>Sleep prediction and schedule will show here after three days of tracking sleep.</p>
          {prediction.days_of_data !== undefined && prediction.days_of_data > 0 && (
            <p className="data-info">You currently have {prediction.days_of_data} day{prediction.days_of_data !== 1 ? 's' : ''} of data.</p>
          )}
        </div>
      ) : prediction.status === 'error' ? (
        <div className="error-state">
          <p>Unable to generate sleep schedule. Please try again later.</p>
        </div>
      ) : (
        <>
          <div className="predicted-slots">
            <h3>Predicted Sleep Periods</h3>

            {(() => {
              // Filter to show sleep slots that haven't ended yet (includes ongoing and future)
              const now = new Date();
              const futureSlots = prediction.predicted_slots.filter(slot =>
                new Date(slot.predicted_end_time) > now
              );

              if (futureSlots.length === 0) {
                return <p className="no-slots">No upcoming sleep periods</p>;
              }

              return (
                <div className="slots-list">
                  {futureSlots.map((slot) => (
                    <div
                      key={slot.id}
                      className={`sleep-slot ${slot.slot_type === 'night_sleep' ? 'night-sleep' : 'nap'}`}
                    >
                      <div className="slot-icon">
                        {slot.slot_type === 'night_sleep' ? (
                          <Moon size={24} weight="fill" />
                        ) : (
                          <Bed size={24} weight="fill" />
                        )}
                      </div>
                      <div className="slot-content">
                        <div className="slot-type">
                          {slot.slot_type === 'night_sleep' ? 'Night Sleep' : 'Nap'}
                        </div>
                        <div className="slot-time">
                          {formatDateTimeRelative(slot.predicted_start_time, user.time_format_24h)} - {formatDateTimeRelative(slot.predicted_end_time, user.time_format_24h)}
                        </div>
                        <div className="slot-duration">
                          {formatDuration(slot.predicted_start_time, slot.predicted_end_time)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default Predictions;
